import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isAndroidApp } from "@/lib/platform";
import { showBanner, hideBanner } from "@/lib/native-ads";

/**
 * Inline AdMob banner trigger.
 *
 * The Capacitor AdMob plugin renders banners as a native overlay (not in the
 * WebView DOM). We only want the banner to appear when the user has scrolled
 * to the very bottom of the page — and it must sit ABOVE the bottom nav, not
 * cover it. The `margin` in showBanner() lifts the native overlay above the
 * 56dp bottom nav + safe-area inset.
 *
 * Reserves ~72px of bottom space so the last bit of content isn't hidden
 * behind the banner when it appears.
 *
 * No-op for paid users, web, and iOS.
 */
export const InlineAdBanner = () => {
  const { user } = useAuth();
  const [isPaid, setIsPaid] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) { setIsPaid(false); return; }
    supabase
      .from("profiles")
      .select("credits_purchased_total")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => setIsPaid(Number(data?.credits_purchased_total ?? 0) > 0));
  }, [user?.id]);

  useEffect(() => {
    if (!isAndroidApp() || isPaid !== false) return;

    let shown = false;
    const THRESHOLD = 8; // px from the absolute bottom
    const check = () => {
      const doc = document.documentElement;
      const scrollBottom = window.scrollY + window.innerHeight;
      const atBottom = scrollBottom >= doc.scrollHeight - THRESHOLD;
      if (atBottom && !shown) {
        shown = true;
        void showBanner();
      } else if (!atBottom && shown) {
        shown = false;
        void hideBanner();
      }
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      void hideBanner();
    };
  }, [isPaid]);

  if (!isAndroidApp() || isPaid !== false) return null;

  // Reserve space so the last bit of content isn't hidden by the overlay banner.
  return <div aria-hidden className="h-[72px] w-full" />;
};

export default InlineAdBanner;
